import React, { FC } from 'react';
import { TypeReplacementPartsComponentsPageData } from '@/app/[locale]/(shop)/account/replacement-parts/[product]/fetchPageData';
import { TypeReplacementPartsFields } from '@/core/generated-types';
import { Link } from '@/navigation';
import Heading, {
  HeadingTag,
  HeadingSize,
} from '@/components/ui/Heading/Heading';
import Image from 'next/image';
import { getAssetForProduct } from '@/lib/api/productCatalog/helpers/playKitProductAssetHelpers';
import { usePathname } from '@/navigation';
import { TypeSubscriberShopPageTranslations } from '@/utils/translations/types/TypeSubscriberShopPageTranslations';
import generateTranslationsObject from '@/utils/translations/generateTranslationObject';
import { getUnpurchasableProducts, getUpdatedProducts } from './utils';
import ReplacementPartsTaggedProductsList from './ReplacementPartsTaggedProductsList';
import ReplacementPartsBreadcrumbTree from './ReplacementPartsBreadcrumbTree';

interface ReplacementPartsComponentsProps extends TypeReplacementPartsFields {
  data: TypeReplacementPartsComponentsPageData;
}

const ReplacementPartsComponents: FC<ReplacementPartsComponentsProps> = (
  props,
) => {
  const {
    data: { products, parentProduct },
    translations,
  } = props;
  const pathname = usePathname();

  const t = generateTranslationsObject(
    translations?.fields,
  ) as TypeSubscriberShopPageTranslations;

  const updatedProducts = getUpdatedProducts(products);
  const unpurchasableProducts = getUnpurchasableProducts(products);

  const parentProductAgeRange =
    parentProduct.metadata.data?.age_tagline || parentProduct.display_age_range;

  return (
    <>
      <ReplacementPartsBreadcrumbTree
        rootText="Find Parts"
        rootUrl="/account/replacement-parts"
        parentProduct={parentProduct}
      />
      {parentProduct ? (
        <>
          <Heading
            tag={HeadingTag.H1}
            size={HeadingSize.H1}
            classes="mt-4 md:mt-8"
            text={parentProduct.name}
          />
          <p className="text-xl">{parentProductAgeRange}</p>
        </>
      ) : null}

      <div className="mt-8 grid grid-cols-1 gap-x-6 gap-y-12 md:mt-12 md:grid-cols-2 lg:grid-cols-3">
        {products?.map(({ product }) => {
          const {
            name,
            external_id,
            anchor,
            display_age_range: displayAgeRange,
          } = product;
          const asset = getAssetForProduct(product);
          return (
            <Link
              key={external_id}
              href={`${pathname}/${anchor}`}
              className="hover:text-indigo-dark"
            >
              {asset ? (
                <div className="relative bg-khaki-tint">
                  <Image
                    src={asset.uri}
                    alt={asset.alt_text || `${name} image`}
                    height={240}
                    width={320}
                    className="mx-auto h-[240px] object-contain"
                  />
                </div>
              ) : null}
              <div className="mt-3">
                <p className="text-xl font-bold">{name}</p>
                <p>{displayAgeRange}</p>
              </div>
            </Link>
          );
        })}
      </div>
      <ReplacementPartsTaggedProductsList
        label={t.updatedProductsLabel}
        products={updatedProducts}
      />
      <ReplacementPartsTaggedProductsList
        label={t.unpurchasableProductsLabel}
        products={unpurchasableProducts}
      />
      <div className="my-10">
        <Link href="/account/replacement-parts" className="text-indigo">
          {t.backToPlayKitsCta}
        </Link>
      </div>
    </>
  );
};

export default ReplacementPartsComponents;
